import { Component, ElementRef, EventEmitter, Input, OnInit, Output, OnDestroy, ViewChild, SimpleChanges, OnChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AmplitudeService } from '@inyova/inyova-shared';
import { GrowAccount, InyovaGrowAccountStatus } from '@inyova/models';
import { TranslateService } from '@ngx-translate/core';

import { debounceTime, filter, Subject, takeUntil } from 'rxjs';

import {
  InyovaGrowFinancialDetail,
  InyovaGrowSubmit,
  InyovaGrowRiskGroups,
  InyovaGrowPercentages,
  InyovaGrowSurveyQuestion,
  InyovaGrowRiskSurveyQuestion
} from '../../models';
import { freelyMonthlyAvailable } from '../../service';

interface FormData {
  investment: { initial: number; savings: number };
  assets: { income: number; assets: number };
  survey: {
    multi: { id: number; selectedAnswerIds: number[] }[];
    optional: { listId: number; selected: boolean; id: number; selectedAnswerId: number | string }[];
    single: { id: number; selectedAnswerId: number }[];
    basic: { id: number; selectedAnswerId: number }[];
    experience: { id: number; selectedAnswerId: number };
    investHorizon: { id: number; selectedAnswerId: number };
  };
}

@Component({
  selector: 'app-grow-risk-form',
  templateUrl: './grow-risk-form.component.html',
  styleUrls: ['./grow-risk-form.component.scss'],
  standalone: false
})
export class GrowRiskFormComponent implements OnInit, OnDestroy, OnChanges {
  @Input() riskPercentages: InyovaGrowPercentages;
  @Input() isSubmitLoading: boolean;
  @Input() isRiskSliderLoaded: boolean;
  @Input() isMobileApp = false;
  @Input() hideInvestCallout: boolean;
  @Input() hideCTA: boolean;
  @Input() riskFormSubmitted: boolean;
  @Input() hideInvestmentAmount = false;
  @Input() investmentPlanData: { initial_investment: number; monthly_investment: number };

  @Output() formSubmitted = new EventEmitter<InyovaGrowSubmit>();
  @Output() changed = new EventEmitter<{ answers: InyovaGrowSurveyQuestion[]; availableAssets: number; initInvest: number }>();
  @Output() riskInputError = new EventEmitter();
  @ViewChild('incomeInputEditable') incomeInputEditable: ElementRef<HTMLInputElement>;
  @ViewChild('assetsInputEditable') assetsInputEditable: ElementRef<HTMLInputElement>;

  protected readonly onDestroy$ = new Subject<void>();

  pauseForm = false;
  assetsEditMode = false;
  acceptConsent = false;
  acceptShortHorizon = false;
  acceptFees = false;
  initialInvestment = [5000, 10000, 50000];
  savingsPlan = [100, 500, 1000, 2000];
  shortHorizon = false;
  showShortHorizonError = true;
  showFeesError = false;
  showConsentError = false;
  incomeEditMode = false;

  riskForm: FormGroup;
  selectedRisk: number | null = null;

  private _growAccount: GrowAccount;
  private _growRisk: { surveyGroups?: InyovaGrowRiskGroups; financialDetail?: InyovaGrowFinancialDetail } = {};

  AVAILABLE_ASSETS_ID = 99;
  visibleQuestionIDs = new Set<number>();

  @Input()
  set growAccount(account: GrowAccount) {
    this._growAccount = account;
    if (account) {
      this.acceptConsent = this.growAccount.investment_risk_consent;
      this.selectedRisk = this.growAccount.stock_split;
      this.acceptFees = this.growAccount.fees_consent;

      if (!this.initialInvestment?.includes(this.growAccount.minimum_investment)) {
        this.initialInvestment = [this.growAccount.minimum_investment, ...this.initialInvestment];
      }
    }
  }

  get growAccount(): GrowAccount {
    return this._growAccount;
  }

  @Input()
  set growRisk(risk: { surveyGroups: InyovaGrowRiskGroups; financialDetail: InyovaGrowFinancialDetail }) {
    this._growRisk = risk;
    if (risk) {
      this.updateFormData(risk);
    }
  }

  get growRisk(): { surveyGroups?: InyovaGrowRiskGroups; financialDetail?: InyovaGrowFinancialDetail } {
    return this._growRisk;
  }

  get initInvestControl() {
    return this.riskForm.get(['investment', 'initial']);
  }

  get savingsControl() {
    return this.riskForm.get(['investment', 'savings']);
  }

  get monthlyIncomeControl() {
    return this.riskForm.get(['assets', 'income']);
  }

  get availableAssetsControl() {
    return this.riskForm.get(['assets', 'assets']);
  }

  get experienceFormGroup() {
    return this.riskForm.get(['survey', 'experience']) as FormGroup;
  }

  get investHorizonFormGroup() {
    return this.riskForm.get(['survey', 'investHorizon']) as FormGroup;
  }

  get optionalQuestionsArray(): FormArray {
    return this.riskForm.get(['survey', 'optional']) as FormArray;
  }

  get singleQuestionsArray(): FormArray {
    return this.riskForm.get(['survey', 'single']) as FormArray;
  }

  get basicQuestionsArray(): FormArray {
    return this.riskForm.get(['survey', 'basic']) as FormArray;
  }

  get multiQuestionsArray(): FormArray {
    return this.riskForm.get(['survey', 'multi']) as FormArray;
  }

  get editGrowRisk() {
    return this.growAccount?.step_status !== InyovaGrowAccountStatus.INITIAL;
  }

  get invalidFreely(): boolean {
    return (
      (this.initInvestControl.dirty || this.availableAssetsControl.touched) &&
      this.availableAssetsControl.valid &&
      this.riskForm.hasError('invalidFreely')
    );
  }

  get isInvestHorizonQuestionVisible(): boolean {
    return this.visibleQuestionIDs.has(this.investHorizonFormGroup.get('id').value);
  }

  constructor(
    private fb: FormBuilder,
    private el: ElementRef,
    private amplitudeService: AmplitudeService,
    private translateService: TranslateService
  ) {
    this.riskForm = this.fb.group(
      {
        investment: this.fb.group({
          initial: [0, [Validators.required]],
          savings: [0]
        }),
        assets: this.fb.group({
          income: [0, [Validators.required, Validators.min(1)]],
          assets: [0, [Validators.required, Validators.min(1)]]
        }),
        survey: this.fb.group({
          optional: this.fb.array([]),
          single: this.fb.array([]),
          basic: this.fb.array([]),
          experience: this.fb.group({
            id: 0,
            selectedAnswerId: ['', [Validators.required]]
          }),
          investHorizon: this.fb.group({
            id: 0,
            selectedAnswerId: ['', Validators.required]
          }),
          multi: this.fb.array([])
        })
      },
      { validators: freelyMonthlyAvailable() }
    );
  }

  ngOnInit(): void {
    this.riskForm.valueChanges
      .pipe(
        filter(() => !this.pauseForm),
        debounceTime(300),
        takeUntil(this.onDestroy$)
      )
      .subscribe((value) => {
        if (this.riskForm.invalid) {
          this.riskInputError.emit();
          return;
        }
        this.changed.emit({
          answers: this.getSurveyAnswers(value as FormData),
          availableAssets: this.availableAssetsControl.value,
          initInvest: this.initInvestControl.value
        });
      });
    if (this.investmentPlanData) {
      this.initInvestControl.patchValue(this.investmentPlanData.initial_investment);
      this.savingsControl.patchValue(this.investmentPlanData.monthly_investment);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['investmentPlanData'] && this.investmentPlanData) {
      if (this.initInvestControl) {
        this.initInvestControl.patchValue(this.investmentPlanData.initial_investment);
      }
      if (this.savingsControl) {
        this.savingsControl.patchValue(this.investmentPlanData.monthly_investment);
      }
    }
  }

  updateFormData(risk: { surveyGroups: InyovaGrowRiskGroups; financialDetail: InyovaGrowFinancialDetail }) {
    if (!this.growAccount) {
      return;
    }
    this.pauseForm = true;

    // set investment
    if (!this.hideInvestmentAmount) {
      this.initInvestControl.setValue(
        this.growAccount.initial_investment === null ? this.growAccount.minimum_investment : this.growAccount.initial_investment
      );
      this.initInvestControl.addValidators([Validators.min(this.growAccount.minimum_investment)]);
      this.savingsControl.setValue(this.growAccount.monthly_investment === null ? 0 : this.growAccount.monthly_investment);
      this.initInvestControl.updateValueAndValidity({ emitEvent: false });
    }

    // set financial
    this.monthlyIncomeControl.setValue(risk.financialDetail?.monthly_income === null ? '' : risk.financialDetail?.monthly_income);
    this.availableAssetsControl.setValue(risk.financialDetail?.freely_cash_available === null ? null : risk.financialDetail?.freely_cash_available);
    this.monthlyIncomeControl.updateValueAndValidity({ emitEvent: false });
    this.availableAssetsControl.updateValueAndValidity({ emitEvent: false });
    // set survey
    this.setSurvey(risk.surveyGroups);
    this.pauseForm = false;

    // If not prefilled, mark it as visible for the UI
    if (this.isMobileApp || risk.financialDetail?.freely_cash_available === null || risk.financialDetail?.freely_cash_available === undefined) {
      this.visibleQuestionIDs.add(this.AVAILABLE_ASSETS_ID);
    }
  }

  setSurvey(surveyGroups: InyovaGrowRiskGroups) {
    this.setExperienceItem(surveyGroups.experience);
    this.setInvestHorizonItem(surveyGroups.investHorizon);
    this.setBasicItems(surveyGroups.basic);
    this.setOptionalItems(surveyGroups.optionalList, surveyGroups.optional);
    this.setSingleItems(surveyGroups.singleChoice);
    this.setMultiItems(surveyGroups.multiChoices);
  }

  setExperienceItem(experience: InyovaGrowRiskSurveyQuestion) {
    this.experienceFormGroup.setValue({
      id: experience.id,
      selectedAnswerId: experience.survey_customer_answers?.answer_value || ''
    });

    // If not prefilled, mark it as visible for the UI
    if (!experience.survey_customer_answers?.answer_value) {
      this.visibleQuestionIDs.add(experience.id);
    }
  }

  setInvestHorizonItem(horizon: InyovaGrowRiskSurveyQuestion) {
    this.investHorizonFormGroup.setValue({
      id: horizon.id,
      selectedAnswerId: horizon.survey_customer_answers?.answer_value || ''
    });

    // If not prefilled, mark it as visible for the UI
    if (!horizon.survey_customer_answers?.answer_value) {
      this.visibleQuestionIDs.add(horizon.id);
    }
  }

  setOptionalItems(optionalList: InyovaGrowRiskSurveyQuestion, optional: InyovaGrowRiskSurveyQuestion[]) {
    // clear FormArray if there are items in it
    this.optionalQuestionsArray.clear();

    optionalList.survey_question_options.forEach((listItem, i) => {
      const formGroup = this.fb.group({
        listId: listItem.id,
        inyovaId: listItem.inyova_id,
        selected: false,
        id: optional[i]?.id ?? null,
        selectedAnswerId: ''
      });

      const hasSelected =
        Array.isArray(optionalList.survey_customer_answers?.answer_value) &&
        (optionalList.survey_customer_answers.answer_value as number[]).includes(listItem.id);

      if (hasSelected) {
        formGroup.get('selected')?.setValue(true);

        const selectedAnswer = optional[i]?.survey_customer_answers?.answer_value;
        const selectedAnswerValue: number | null = Array.isArray(selectedAnswer)
          ? (selectedAnswer[0] ?? null)
          : typeof selectedAnswer === 'number'
            ? selectedAnswer
            : null;

        if (selectedAnswer) {
          const selectedAnswerIdControl = formGroup.get('selectedAnswerId');
          selectedAnswerIdControl?.setValue(selectedAnswerValue as unknown as string);
          selectedAnswerIdControl?.addValidators(Validators.required);
          selectedAnswerIdControl?.updateValueAndValidity();
        }
      }

      this.optionalQuestionsArray.push(formGroup);
    });
  }

  setSingleItems(single: InyovaGrowRiskSurveyQuestion[] = []) {
    // clear FormArray if there are items in it
    if (this.singleQuestionsArray.length > 0) {
      this.singleQuestionsArray.clear();
    }
    single.forEach((item) => {
      const formGroup = this.fb.group({
        id: item.id,
        selectedAnswerId: ['', Validators.required]
      });

      if (item.survey_customer_answers !== null) {
        const selectedAnswerIdControl = formGroup.get('selectedAnswerId');
        selectedAnswerIdControl.setValue(item.survey_customer_answers.answer_value as unknown as string);
        selectedAnswerIdControl.updateValueAndValidity();
      }

      this.singleQuestionsArray.push(formGroup);

      // Determine if the question is "prefilled"
      const isPrefilled = !!item.survey_customer_answers?.answer_value;

      // If not prefilled, mark it as visible for the UI
      if (!isPrefilled) {
        this.visibleQuestionIDs.add(item.id);
      }
    });
  }

  setBasicItems(basic: InyovaGrowRiskSurveyQuestion[] = []) {
    this.basicQuestionsArray.clear();

    basic.forEach((item) => {
      const group = this.fb.group({
        id: item.id,
        selectedAnswerId: [item.survey_customer_answers?.answer_value ?? '', Validators.required]
      });

      this.basicQuestionsArray.push(group);

      // Determine if the question is "prefilled"
      const isPrefilled = !!item.survey_customer_answers?.answer_value;

      if (!isPrefilled) {
        this.visibleQuestionIDs.add(item.id);
      }
    });
  }

  setMultiItems(multi: InyovaGrowRiskSurveyQuestion[] = []) {
    // clear FormArray if there are items in it
    if (this.multiQuestionsArray.length > 0) {
      this.multiQuestionsArray.clear();
    }
    multi.forEach((item) => {
      const formGroup = this.fb.group({
        id: item.id,
        selectedAnswerIds: this.fb.array([], [Validators.required])
      });

      if (item.survey_customer_answers !== null) {
        const selectedAnswerIdsArray = formGroup.get('selectedAnswerIds') as FormArray;
        [item.survey_customer_answers.answer_value]
          .flatMap((value) => (Array.isArray(value) ? value : [value]))
          ?.forEach((id) => {
            selectedAnswerIdsArray.push(this.fb.control(id));
          });
        formGroup.get('selectedAnswerIds').updateValueAndValidity();
      }

      this.multiQuestionsArray.push(formGroup);

      // If not prefilled, mark it as visible for the UI
      if (
        (Array.isArray(item.survey_customer_answers?.answer_value) && item.survey_customer_answers?.answer_value.length === 0) ||
        !item.survey_customer_answers?.answer_value
      ) {
        this.visibleQuestionIDs.add(item.id);
      }
    });
  }

  onToggleOptional(groupIndex: number) {
    const group = this.optionalQuestionsArray.controls[groupIndex] as FormGroup;
    const expandList = group.get('selected');
    const selectedAnswerId = group.get('selectedAnswerId');

    if (expandList.value) {
      expandList.setValue(false);
      if (selectedAnswerId.hasValidator(Validators.required)) {
        selectedAnswerId.setValue('', { emitEvent: false });
        selectedAnswerId.removeValidators([Validators.required]);
        selectedAnswerId.updateValueAndValidity({ emitEvent: false });
      }
    } else {
      expandList.setValue(true, { emitEvent: false });
      if (group.get('inyovaId').value === 135) {
        // if "None of These" is selected no further selection is needed
        selectedAnswerId.removeValidators([Validators.required]);
      } else {
        selectedAnswerId.addValidators([Validators.required]);
      }

      selectedAnswerId.updateValueAndValidity();
    }
  }

  getFormGroup(index: number) {
    return this.multiQuestionsArray.controls[index] as FormGroup;
  }

  getSurveyAnswers(formValue: FormData): InyovaGrowSurveyQuestion[] {
    let answers: InyovaGrowSurveyQuestion[] = [];

    //experience
    answers.push({ id: formValue.survey.experience.id, survey_customer_answers: formValue.survey.experience.selectedAnswerId });
    //investment horizon
    answers.push({ id: formValue.survey.investHorizon.id, survey_customer_answers: formValue.survey.investHorizon.selectedAnswerId });
    // optional
    const optionalParent: { id: number; survey_customer_answers: number[] } = {
      id: this.growRisk.surveyGroups.optionalList.id,
      survey_customer_answers: []
    };
    formValue.survey.optional.forEach((item) => {
      if (item.selected && item.id) {
        optionalParent.survey_customer_answers.push(item.listId);
        answers.push({ id: item.id, survey_customer_answers: item.selectedAnswerId as number });
      }
    });
    if (optionalParent.survey_customer_answers.length > 0) {
      answers = [optionalParent, ...answers];
    }
    // basic
    formValue.survey.basic.forEach((item) => {
      answers.push({ id: item.id, survey_customer_answers: item.selectedAnswerId });
    });
    // single
    formValue.survey.single.forEach((item) => {
      answers.push({ id: item.id, survey_customer_answers: item.selectedAnswerId });
    });
    // multi
    formValue.survey.multi.forEach((item) => {
      answers.push({ id: item.id, survey_customer_answers: item.selectedAnswerIds });
    });

    return answers;
  }

  onRiskChange({ selectedRisk, acceptConsent, acceptFees }: { selectedRisk: number; acceptConsent: boolean; acceptFees: boolean }) {
    this.selectedRisk = selectedRisk;
    this.acceptConsent = acceptConsent;
    this.acceptFees = acceptFees;

    if (this.acceptConsent) {
      this.showConsentError = false;
    }
    if (this.acceptFees) {
      this.showFeesError = false;
    }
  }

  onInvestHorizonChange({ acceptShortHorizon }: { acceptShortHorizon: boolean }) {
    this.acceptShortHorizon = acceptShortHorizon;
    if (this.acceptShortHorizon) {
      this.showShortHorizonError = false;
    }
  }

  scrollIntoView(element: string) {
    const el: HTMLElement | null = this.el.nativeElement.querySelector(element);
    if (el !== null) {
      el.scrollIntoView({
        behavior: 'smooth',
        inline: 'start'
      });
    }
  }

  enterIncomeEditMode() {
    this.incomeEditMode = true;
    // wait for the input to be rendered, then focus
    setTimeout(() => {
      const el = this.incomeInputEditable?.nativeElement;
      if (el) {
        el.focus();
      }
    });
  }

  enterAssetsEditMode() {
    this.assetsEditMode = true;
    // wait for the input to be rendered, then focus
    setTimeout(() => {
      const el = this.assetsInputEditable?.nativeElement;
      if (el) {
        el.focus();
      }
    });
  }

  onSubmit() {
    this.showConsentError = false;
    this.showFeesError = false;
    this.riskFormSubmitted = true;
    if (this.riskForm.invalid || this.isSubmitLoading || typeof this.riskPercentages === 'undefined') {
      this.scrollIntoView('form .ng-invalid');
      return;
    }

    if (!this.acceptShortHorizon && this.isInvestHorizonQuestionVisible) {
      this.showShortHorizonError = true;
      this.scrollIntoView('app-inyova-grow-investment-horizon-box');
      return;
    }

    if (!this.acceptFees) {
      this.showFeesError = true;
      this.scrollIntoView('app-inyova-grow-risk-slider');
      return;
    }

    if (this.selectedRisk !== null && this.selectedRisk > this.riskPercentages.recommended_projects_percentage && !this.acceptConsent) {
      this.showConsentError = true;
      return;
    }

    const selectedRisk = this.selectedRisk ?? this.riskPercentages.recommended_projects_percentage;
    const requiresConsent = selectedRisk > this.riskPercentages.recommended_projects_percentage;

    const data: InyovaGrowSubmit = {
      interestAccount: {
        id: this.growAccount.id,
        type: 'interest_account',
        initial_investment: this.initInvestControl.value,
        monthly_investment: this.savingsControl.value,
        projects_percentage: selectedRisk,
        fees_consent: this.acceptFees,
        investment_risk_consent: requiresConsent ? this.acceptConsent : false,
        risk_consent_text: requiresConsent ? this.translateService.instant('GROW.riskProfile.profile.dynamicConsent') : ''
      },
      riskSurvey: {
        type: 'risk_survey',
        survey_questions: this.getSurveyAnswers(this.riskForm.value as FormData)
      },
      ...(this.growRisk.financialDetail && {
        financialDetail: {
          id: this.growRisk.financialDetail.id,
          type: 'financial_detail',
          monthly_income: this.monthlyIncomeControl.value,
          freely_cash_available: this.availableAssetsControl.value
        }
      })
    };
    this.formSubmitted.emit(data);
  }

  trackTooltip(name: string) {
    this.amplitudeService.trackEvent('Tooltip Info Consulted', {
      tooltip_name: name,
      screen: 'Grow Risk form',
      consultation_method: 'click'
    });
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
